import clsx from 'clsx';
import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';

import FeedbackOptionsScreen from './FeedbackOptionsScreen';
import FeedbackForm from './FeedbackForm';
import FeedbackSuccessScreen from './FeedbackSuccessScreen';
import { sendFeedback, prepareUserName } from '../../services/feedbackService';
import { useFeedbackLogger } from '../../hooks/useFeedbackDrawer';

const UserFeedbackDrawer = ({ isOpen, onClose }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { logUserFeedback } = useFeedbackLogger();

  const drawerRef = useRef(null);
  const { user } = useSelector((state) => state.user);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      document.body.style.overflow = 'hidden';
    } else {
      setIsVisible(false);
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleClose = () => {
    setIsClosing(true);

    setTimeout(() => {
      onClose();
      setIsClosing(false);
      setSelectedOption(null);
      setShowForm(false);
      setShowSuccess(false);
    }, 300);
  };

  const handleOptionClick = (option) => {
    setSelectedOption(option);
    setShowForm(true);
  };

  const handleFormSubmit = async (formData) => {
    setIsSubmitting(true);

    try {
      const userName = prepareUserName(user, formData.email);

      await sendFeedback(
        {
          name: userName,
          email: formData.email,
          message: formData.message
        },
        selectedOption.id
      );

      const orderId = JSON.parse(localStorage.getItem('cartItems'))?._id; 

      logUserFeedback(
        orderId,
        {
        name: userName,
        email: formData.email.trim(),
        message: formData.message
      }, selectedOption.id);


      setShowSuccess(true);

      setTimeout(() => {
        handleClose();
      }, 4500);

    } catch (error) {
      console.error('Erreur lors de l\'envoi du feedback:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      handleClose();
    }
  };

  if (!isOpen && !isClosing) return null;

  return createPortal(
    <div
      className={`fixed inset-0 z-[300000] transition-all duration-300 ${
        isVisible && !isClosing
          ? 'bg-black/50 backdrop-blur-sm'
          : 'bg-transparent'
      }`}
      onClick={handleClose}
      onKeyDown={handleKeyDown}
    >
      {/* Bottom Sheet Container */}
      <div
        ref={drawerRef}
        className={clsx(
          "fixed bottom-0 min-h-[500px] left-0 right-0 rounded-t-2xl shadow-2xl transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]",
          {
            "translate-y-0 opacity-100": isVisible && !isClosing,
            "translate-y-full opacity-0": !(isVisible && !isClosing),
            "bg-white": !showSuccess,
            "bg-transparent": showSuccess
          }
        )}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.key === 'Enter' && e.stopPropagation()}
      >
        <div className="relative h-full">
          {/* Options Screen */}
          <FeedbackOptionsScreen
            onClose={handleClose}
            onOptionSelect={handleOptionClick}
            isVisible={!showForm && !showSuccess}
          />

          {/* Form Screen */}
          <FeedbackForm
            selectedOption={selectedOption}
            onClose={handleClose}
            onSubmit={handleFormSubmit}
            isVisible={showForm && !showSuccess}
            isSubmitting={isSubmitting}
            initialEmail={user?.email || ''}
          />
        </div>
      </div>

      {/* Success Screen */}
      <FeedbackSuccessScreen
        selectedOption={selectedOption}
        isVisible={showSuccess}
      />
    </div>,
    document.body
  );
};

export default UserFeedbackDrawer;